<template>
  <div>
    <SideBar :sidebar-open="sidebarOpen" @update:sidebar-open="(value) => (sidebarOpen = value)" />

    <div class="xl:pl-72">
      <div
        class="sticky top-0 z-40 flex h-16 shrink-0 items-center gap-x-6 border-b border-white/5 bg-background px-4 shadow-sm sm:px-6 lg:px-8"
      >
        <button type="button" id="sidebar" class="-m-2.5 p-2.5 text-white xl:hidden" @click="sidebarOpen = true">
          <span class="sr-only">{{ t("msg.openSidebar") }}</span>
          <Bars3Icon class="h-5 w-5" aria-hidden="true" />
        </button>

        <SearchBar :model-value="search" @update:model-value="(text) => (search = text)" />
      </div>

      <AdminHeader
        :alert-order="['unlicensed', 'agentIncompatible', 'userLimitExceeded', 'userErr', 'serverUpdate']"
        :alert-triggers="{ userErr: () => userCount > userLimit && !hasSamlUsers }"
      >
        <template #userErr>
          <div
            class="flex w-full flex-col gap-3 bg-primary px-4 py-3 text-primary-foreground md:flex-row md:items-center"
          >
            <div class="flex flex-1 items-center gap-3">
              <div class="flex-shrink-0">
                <ExclamationTriangleIcon class="h-8 w-8" />
              </div>

              <div class="min-w-0 flex-1">
                <div class="text-sm font-semibold">
                  {{ t("pages.header.userCountExceedingLimit", { userCount, userLimit }) }}
                </div>
                <div class="mt-1 text-xs text-primary-foreground/75">
                  <span v-if="userOverage > 0">
                    {{
                      t("pages.header.additionalUsersAllowed", {
                        userOverage: userLimit + userOverage - userCount,
                        userOveragePercentage,
                      })
                    }}
                  </span>
                  <span v-else>
                    {{ t("pages.header.newUserCreationBlocked") }}
                  </span>
                </div>
              </div>
            </div>

            <div class="flex items-center gap-2 md:flex-shrink-0">
              <a
                href="https://licensing.knocknoc.io"
                target="_blank"
                rel="noopener noreferrer"
                class="inline-flex items-center gap-2 rounded-md border border-primary-foreground/30 px-4 py-2 text-sm font-medium text-primary-foreground no-underline transition-colors hover:bg-primary-foreground/10 focus:outline-none focus:ring-2 focus:ring-primary-foreground/50 max-md:w-full"
              >
                <DocumentTextIcon class="h-4 w-4" />
                {{ t("pages.users.header.upgradeLicense") }}
              </a>
            </div>
          </div>
        </template>
      </AdminHeader>

      <main class="flex flex-col space-y-8 px-12 py-10">
        <div class="flex items-start justify-between">
          <div class="flex flex-col space-y-3">
            <div class="flex items-center gap-3">
              <h1
                tabindex="-1"
                class="no-focus-outline scroll-m-20 text-3xl font-bold tracking-tight"
                ref="initialFocus"
              >
                {{ t("pages.users.heading.title") }}
              </h1>
              <a
                class="ml-2 inline-flex items-center gap-1 rounded-md border border-transparent bg-secondary px-2.5 py-0.5 text-xs font-semibold text-secondary-foreground transition-colors hover:bg-secondary/80 focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2"
                href="https://docs.knocknoc.io/books/admin-guide/page/create-users"
                target="_blank"
                :aria-label="`${t('pages.users.docs')} (${t('common.opensInNewTab')})`"
              >
                {{ t("common.docs") }}
                <ArrowTopRightOnSquareIcon class="h-3 w-3" />
                <span class="sr-only">{{ t("common.opensInNewTab") }}</span>
              </a>
            </div>

            <div class="max-w-2xl">
              <p class="text-base leading-relaxed text-muted-foreground">
                {{ t("pages.users.heading.description") }}
              </p>
            </div>
          </div>

          <div class="flex flex-wrap-reverse justify-end gap-2">
            <button
              type="button"
              @click="invalidateSessions()"
              class="inline-flex h-10 items-center justify-center gap-2 whitespace-nowrap rounded-lg bg-primary px-4 py-2 text-sm font-medium text-primary-foreground shadow-sm ring-offset-background transition-all duration-200 hover:bg-primary/90 hover:shadow-md focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50 [&_svg]:pointer-events-none [&_svg]:size-4 [&_svg]:shrink-0"
            >
              {{ t("pages.users.heading.actions.invalidate") }}
            </button>
            <button
              type="button"
              @click="handleToggleCreateDialog()"
              class="inline-flex h-10 items-center justify-center gap-2 whitespace-nowrap rounded-lg bg-primary px-4 py-2 text-sm font-medium text-primary-foreground shadow-sm ring-offset-background transition-all duration-200 hover:bg-primary/90 hover:shadow-md focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50 [&_svg]:pointer-events-none [&_svg]:size-4 [&_svg]:shrink-0"
            >
              {{ t("pages.users.heading.actions.create") }}
            </button>
          </div>
        </div>

        <Table class="mb-16">
          <TableHeader>
            <TableRow>
              <TableHead :aria-sort="sortBy === 'Name' ? (sortDesc ? 'descending' : 'ascending') : undefined">
                <SortButton @click="handleSortChange('Name')">
                  {{ t("fields.users.name") }}
                </SortButton>
              </TableHead>
              <TableHead :aria-sort="sortBy === 'Company' ? (sortDesc ? 'descending' : 'ascending') : undefined">
                <SortButton @click="handleSortChange('Company')">
                  {{ t("fields.users.company") }}
                </SortButton>
              </TableHead>
              <TableHead :aria-sort="sortBy === 'Duration' ? (sortDesc ? 'descending' : 'ascending') : undefined">
                <SortButton @click="handleSortChange('Duration')">
                  {{ t("fields.users.sessionDuration") }}
                </SortButton>
              </TableHead>
              <TableHead :aria-sort="sortBy === 'AuthType' ? (sortDesc ? 'descending' : 'ascending') : undefined">
                <SortButton @click="handleSortChange('AuthType')">
                  {{ t("fields.users.authType") }}
                </SortButton>
              </TableHead>
              <TableHead :aria-sort="sortBy === 'UserName' ? (sortDesc ? 'descending' : 'ascending') : undefined">
                <SortButton @click="handleSortChange('UserName')">
                  {{ t("fields.users.username") }}
                </SortButton>
              </TableHead>
              <TableHead :aria-sort="sortBy === 'LastLogin' ? (sortDesc ? 'descending' : 'ascending') : undefined">
                <SortButton @click="handleSortChange('LastLogin')">
                  {{ t("fields.users.lastLogin") }}
                </SortButton>
              </TableHead>
              <TableHead :aria-sort="sortBy === 'SessionIps' ? (sortDesc ? 'descending' : 'ascending') : undefined">
                <SortButton @click="handleSortChange('SessionIps')">
                  {{ t("fields.users.activeIps") }}
                </SortButton>
              </TableHead>
              <TableHead class="relative">
                <span class="sr-only">{{ t("msg.edit") }}</span>
              </TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            <TableRow v-if="!filterData?.length">
              <TableCell colspan="8" class="text-center text-muted-foreground">
                <div class="flex items-center justify-center gap-2">
                  <InformationCircleIcon class="h-4 w-4" aria-hidden="true" />
                  <span>
                    {{ t("common.getStarted", { button: t("pages.users.heading.actions.create") }) }}
                  </span>
                </div>
              </TableCell>
            </TableRow>
            <TableRow v-else v-for="(user, index) in filterData" :key="user.ID">
              <TableCell
                :tooltip-text="!user.Licensed ? 'This user is disabled due to license restrictions' : ''"
                :class="{ 'text-gray-500 opacity-50': !user.Licensed }"
              >
                {{ user.Name }}
              </TableCell>
              <TableCell
                :tooltip-text="!user.Licensed ? 'This user is disabled due to license restrictions' : ''"
                :class="{ 'text-gray-500 opacity-50': !user.Licensed }"
              >
                {{ user.Company }}
              </TableCell>
              <TableCell
                :tooltip-text="!user.Licensed ? 'This user is disabled due to license restrictions' : ''"
                :class="{ 'text-gray-500 opacity-50': !user.Licensed }"
              >
                {{ user.Duration }}
              </TableCell>
              <TableCell
                :tooltip-text="!user.Licensed ? 'This user is disabled due to license restrictions' : ''"
                :class="{ 'text-gray-500 opacity-50': !user.Licensed }"
              >
                {{ user.AuthType }}
              </TableCell>
              <TableCell
                :tooltip-text="!user.Licensed ? 'This user is disabled due to license restrictions' : ''"
                :class="{ 'text-gray-500 opacity-50': !user.Licensed }"
              >
                {{ user.UserName }}
              </TableCell>
              <TableCell
                :tooltip-text="!user.Licensed ? 'This user is disabled due to license restrictions' : ''"
                :class="{ 'text-gray-500 opacity-50': !user.Licensed }"
              >
                {{
                  user.LastLogin
                    ? DateTime.fromISO(user.LastLogin).toLocaleString(DateTime.DATETIME_MED)
                    : t("msg.never")
                }}
              </TableCell>
              <TableCell
                :tooltip-text="!user.Licensed ? 'This user is disabled due to license restrictions' : ''"
                :class="{ 'text-gray-500 opacity-50': !user.Licensed }"
              >
                <template v-if="user.ActiveSession">
                  {{ user?.SessionIps?.reduce((x, acc) => `${acc}${acc?.length > 0 ? ", " : ""}${x}`) }}
                </template>
              </TableCell>
              <TableCell>
                <Menu as="div" class="relative inline-block text-left">
                  <MenuButton
                    ref="menuButtonsRefs"
                    :aria-label="t('common.actions')"
                    class="inline-flex h-10 w-10 items-center justify-center gap-2 whitespace-nowrap rounded-full text-sm font-medium ring-offset-background transition-colors hover:bg-accent hover:text-accent-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50 [&_svg]:pointer-events-none [&_svg]:size-4 [&_svg]:shrink-0"
                    @click="(e) => e.stopPropagation()"
                  >
                    <EllipsisVerticalIcon class="h-4 w-4" />
                  </MenuButton>

                  <Teleport to="body">
                    <MenuItems
                      as="div"
                      class="fixed z-[100] w-56 origin-top-right divide-y divide-gray-100 rounded-md bg-background shadow-lg ring-1 ring-black/5 focus:outline-none"
                      :style="{
                        top: (menuItemPositions[index]?.top || 0) + 40 + 'px',
                        left: (menuItemPositions[index]?.left || 0) - 120 + 'px',
                      }"
                    >
                      <div class="px-1 py-1">
                        <MenuItem>
                          <button
                            class="group flex w-full items-center rounded-md px-2 py-2 text-sm hover:bg-primary/20"
                            @click="
                              currentUser = user;
                              fetchUserData(user.ID);
                              viewModal = true;
                            "
                          >
                            <EyeIcon class="mr-2 h-5 w-5" />
                            {{ t("pages.users.tables.users.actions.view") }}
                          </button>
                        </MenuItem>

                        <MenuItem>
                          <button
                            class="group flex w-full items-center rounded-md px-2 py-2 text-sm hover:bg-primary/20"
                            @click="
                              currentUser = user;
                              handleToggleCreateDialog();
                            "
                          >
                            <PencilSquareIcon class="mr-2 h-5 w-5" />
                            {{ t("pages.users.tables.users.actions.edit") }}
                          </button>
                        </MenuItem>

                        <MenuItem v-if="user.TOTPEnabled">
                          <button
                            class="group flex w-full items-center rounded-md px-2 py-2 text-sm hover:bg-primary/20"
                            @click="
                              currentUser = user;
                              resetOtpModal = true;
                            "
                          >
                            <KeyIcon class="mr-2 h-5 w-5" />
                            {{ t("pages.users.tables.users.actions.2fa") }}
                          </button>
                        </MenuItem>

                        <MenuItem>
                          <button
                            class="group flex w-full items-center rounded-md px-2 py-2 text-sm hover:bg-primary/20"
                            @click="
                              currentUser = user;
                              deleteModal = true;
                            "
                            aria-label="Delete user"
                          >
                            <TrashIcon class="mr-2 h-5 w-5" />
                            {{ t("pages.users.tables.users.actions.delete") }}
                          </button>
                        </MenuItem>
                      </div>
                    </MenuItems>
                  </Teleport>
                </Menu>
              </TableCell>
            </TableRow>
          </TableBody>
        </Table>
      </main>
    </div>
  </div>

  <Dialog :show="viewModal" v-if="currentUser">
    <DialogOverlay />

    <DialogPanel>
      <DialogTitle>{{ t("msg.userInfo") }}</DialogTitle>

      <div class="max-w-2xl overflow-x-auto">
        <table class="min-w-full divide-y divide-gray-700">
          <tbody class="divide-y divide-gray-800 text-sm">
            <tr>
              <td class="font-semibold">{{ t("fields.users.name") }}</td>
              <td>{{ currentUser.Name }}</td>
            </tr>
            <tr>
              <td class="font-semibold">{{ t("fields.users.company") }}</td>
              <td>{{ currentUser.Company }}</td>
            </tr>
            <tr>
              <td class="font-semibold">
                {{ t("fields.users.sessionDuration") }}
              </td>
              <td>{{ currentUser.Duration }}</td>
            </tr>
            <tr>
              <td class="font-semibold">{{ t("fields.users.sessionLimit") }}</td>
              <td>
                {{ currentUser.SessionLimit == null ? t("msg.unlimited") : currentUser.SessionLimit }}
              </td>
            </tr>
            <tr>
              <td class="font-semibold">{{ t("fields.users.authType") }}</td>
              <td>{{ currentUser.AuthType }}</td>
            </tr>
            <tr>
              <td class="font-semibold">{{ t("fields.users.username") }}</td>
              <td>{{ currentUser.UserName }}</td>
            </tr>
            <tr v-if="currentUser.AuthType !== 'SAML' && currentUser.AuthType !== 'apikey'">
              <td class="font-semibold">{{ t("fields.users.totpEnabled") }}</td>
              <td>{{ currentUser.TOTPEnabled ? t("msg.yes") : t("msg.no") }}</td>
            </tr>
            <tr>
              <td class="font-semibold">{{ t("fields.users.createdAt") }}</td>
              <td>
                {{
                  currentUser.CreatedAt
                    ? DateTime.fromISO(currentUser.CreatedAt).toLocaleString(DateTime.DATETIME_MED)
                    : t("msg.unknown")
                }}
              </td>
            </tr>
            <tr>
              <td class="font-semibold">{{ t("fields.users.lastLogin") }}</td>
              <td>
                {{
                  currentUser.LastLogin
                    ? DateTime.fromISO(currentUser.LastLogin).toLocaleString(DateTime.DATETIME_MED_WITH_SECONDS)
                    : t("msg.never")
                }}
              </td>
            </tr>
            <tr>
              <td class="font-semibold">{{ t("msg.groups") }}</td>
              <td>{{ currentUserGroups.map((g) => g.Name).join(", ") }}</td>
            </tr>
            <tr>
              <td class="font-semibold">{{ t("msg.acls") }}</td>
              <td>{{ currentUserAcls.map((a) => a.Description).join(", ") }}</td>
            </tr>
          </tbody>
        </table>
        <br />
      </div>

      <DialogTitle>{{ t("fields.sessions.sessions") }}</DialogTitle>

      <div class="max-w-2xl overflow-x-auto">
        <table class="min-w-full divide-y divide-gray-700">
          <thead>
            <tr class="text-left font-semibold">
              <th scope="col">{{ t("fields.sessions.createdAt") }}</th>
              <th scope="col">{{ t("fields.sessions.endsAt") }}</th>
              <th scope="col" class="relative">
                <span class="sr-only">{{ t("msg.edit") }}</span>
              </th>
            </tr>
          </thead>
          <tbody class="divide-y divide-gray-800 text-sm">
            <tr v-for="session in currentUserSessions">
              <td>{{ DateTime.fromISO(session.Created).toFormat("ff") }}</td>
              <td>{{ DateTime.fromISO(session.Ends).toFormat("ff") }}</td>
              <td class="relative py-4 pl-3 pr-4 text-right font-medium">
                <a href="#" class="text-[#e05454]" @click="deleteSession(session.ID)">{{ t("msg.delete") }}</a>
              </td>
            </tr>
          </tbody>
        </table>
      </div>

      <button
        type="button"
        @click="closeModal"
        ref="cancelButtonRef"
        class="mt-3 inline-flex w-full justify-center rounded-md bg-white px-3 py-2 text-sm font-semibold text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 hover:bg-gray-50 sm:col-start-1 sm:mt-0"
      >
        {{ t("common.close") }}
      </button>
    </DialogPanel>
  </Dialog>

  <Dialog :show="deleteModal" v-if="currentUser">
    <DialogOverlay />

    <DialogPanel>
      <DialogTitle>
        {{ t("msg.deleteUser") }}
      </DialogTitle>

      <DialogDescription>{{ t("msg.confirmDelete", { name: currentUser.Name }) }}</DialogDescription>

      <div class="flex space-x-3">
        <button
          type="button"
          @click="closeModal"
          ref="cancelButtonRef"
          class="inline-flex w-full justify-center rounded-md bg-white px-3 py-2 text-sm font-semibold text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 hover:bg-gray-50 sm:col-start-1 sm:mt-0"
        >
          {{ t("msg.cancel") }}
        </button>

        <button
          type="button"
          @click="deleteUser()"
          class="inline-flex w-full justify-center rounded-md bg-primary px-3 py-2 text-sm font-semibold text-white shadow-sm focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[#e05454] sm:col-start-2"
        >
          {{ t("msg.delete") }}
        </button>
      </div>
    </DialogPanel>
  </Dialog>

  <Dialog :show="resetOtpModal" v-if="currentUser">
    <DialogOverlay />

    <DialogPanel>
      <DialogTitle>
        {{ t("msg.reset2fa") }}
      </DialogTitle>

      <DialogDescription>
        {{ t("msg.reset2FAConfirmation") }}
      </DialogDescription>

      <div class="flex space-x-3">
        <button
          type="button"
          @click="closeModal"
          ref="cancelButtonRef"
          class="inline-flex w-full justify-center rounded-md bg-white px-3 py-2 text-sm font-semibold text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 hover:bg-gray-50 sm:col-start-1 sm:mt-0"
        >
          {{ t("msg.cancel") }}
        </button>

        <button
          type="button"
          @click="resetOtp()"
          class="inline-flex w-full justify-center rounded-md bg-primary px-3 py-2 text-sm font-semibold text-white shadow-sm focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[#e05454] sm:col-start-2"
        >
          {{ t("msg.reset") }}
        </button>
      </div>
    </DialogPanel>
  </Dialog>

  <UserCreateDialog
    v-if="isCreateDialogOpen"
    :show="isCreateDialogOpen"
    @toggle-show="handleToggleCreateDialog"
    @save="handleSave"
    :value="currentUser"
  />

  <ErrorNotification :error="errorMessage" :title="t('msg.error')" @update:error="(input) => (errorMessage = input)" />

  <InfoNotification :text="infoMessage" @update:info="(input) => (infoMessage = input)" />
</template>

<script setup>
import { DialogDescription, Menu, MenuButton, MenuItem, MenuItems } from "@headlessui/vue";
import {
  ArrowTopRightOnSquareIcon,
  Bars3Icon,
  EllipsisVerticalIcon,
  EyeIcon,
  KeyIcon,
  PencilSquareIcon,
  TrashIcon,
} from "@heroicons/vue/24/solid";
import { DateTime } from "luxon";
import { computed, nextTick, onMounted, onUnmounted, ref, watch } from "vue";
import { useI18n } from "vue-i18n";

import globals from "../../globals";

import SideBar from "../../components/SideBar.vue";
import SearchBar from "../../components/SearchBar.vue";
import ErrorNotification from "../../components/ErrorNotification.vue";
import InfoNotification from "../../components/InfoNotification.vue";
import userService from "../../services/user.service.ts";
import UserCreateDialog from "../../components/UserCreateDialog.vue";
import Dialog from "../../components/ui/dialog/Dialog.vue";
import DialogOverlay from "../../components/ui/dialog/DialogOverlay.vue";
import DialogPanel from "../../components/ui/dialog/DialogPanel.vue";
import DialogTitle from "../../components/ui/dialog/DialogTitle.vue";
import Table from "../../components/ui/table/Table.vue";
import TableHeader from "../../components/ui/table/TableHeader.vue";
import TableBody from "../../components/ui/table/TableBody.vue";
import TableRow from "../../components/ui/table/TableRow.vue";
import TableHead from "../../components/ui/table/TableHead.vue";
import TableCell from "../../components/ui/table/TableCell.vue";
import SortButton from "../../components/ui/table/SortButton.vue";
import AdminHeader from "../../components/AdminHeader.vue";
import { useUserStore } from "../../pinia/user.ts";
import { useAdminStore } from "../../pinia/admin.ts";
import { DocumentTextIcon, ExclamationTriangleIcon, InformationCircleIcon } from "@heroicons/vue/24/outline";

const { t } = useI18n();

const userStore = useUserStore();
const adminStore = useAdminStore();

const initialFocus = ref(null);

const errorMessage = ref("");
const infoMessage = ref("");

const search = ref("");
const sidebarOpen = ref(false);

const menuButtonsRefs = ref([]);
const menuItemPositions = ref([]);
function updateMenuItemPositions() {
  menuItemPositions.value = menuButtonsRefs.value.map((ref) => ref.el.getBoundingClientRect());
}

const isCreateDialogOpen = ref(false);
function handleToggleCreateDialog() {
  isCreateDialogOpen.value = !isCreateDialogOpen.value;
  if (isCreateDialogOpen.value === false) {
    currentUser.value = null;
  }
}

const resetOtpModal = ref(false);
const deleteModal = ref(false);
const viewModal = ref(false);

const currentUser = ref(null);
const currentUserGroups = ref([]);
const currentUserAcls = ref([]);
const currentUserSessions = ref([]);

const userCount = computed(() => adminStore.user?.UserCount);
const hasSamlUsers = computed(() => userStore.users?.data?.some((user) => user.AuthType === "SAML"));
const userLimit = computed(() => adminStore.user?.Licensing?.UserLimit);
const userOverage = computed(() => adminStore.user?.Licensing?.UserLimitOverage);
const userOveragePercentage = computed(() => adminStore.user?.Licensing?.UserLimitOveragePercentage);

const users = computed(() => userStore.users?.data);

const name = ref("");
const authType = ref("");
const userName = ref("");

watch([currentUser, authType], ([currentUser, authType]) => {
  if (currentUser === null && (authType === "local" || authType === "LDAP")) {
    userName.value = name.value.split(" ").join(".").toLowerCase();
  }
});

// const userTableColumns = [
// { key: "Name", label: t("fields.users.name"), sortable: true },
// { key: "Company", label: t("fields.users.company"), sortable: true },
// { key: "Duration", label: t("fields.users.sessionDuration"), sortable: true },
// { key: "AuthType", label: t("fields.users.authType"), sortable: true },
// { key: "UserName", label: t("fields.users.username"), sortable: true },
// { key: "LastLogin", label: t("fields.users.lastLogin"), sortable: true },
// { key: "ActiveSession", label: t("fields.users.activeSession"), sortable: true },
// { key: "Edit", hidden: true },
// ];

const closeModal = () => {
  viewModal.value = false;
  isCreateDialogOpen.value = false;
  deleteModal.value = false;
  resetOtpModal.value = false;
  name.value = "";
  authType.value = "";
  userName.value = "";
  currentUser.value = null;
};

const deleteUser = () => {
  if (currentUser.value !== null) {
    userService.deleteUser(currentUser.value.ID).then(
      () => {
        fetchData();
        infoMessage.value = t("msg.userDeleted");
      },
      (error) => (errorMessage.value = globals.renderError(t, error)),
    );
  }
  deleteModal.value = false;
  currentUser.value = null;
};

const resetOtp = () => {
  if (currentUser.value !== null) {
    userService.resetOtp(currentUser.value.ID).then(
      () => {
        fetchData();
        infoMessage.value = t("msg.2faReset");
      },
      (error) => (errorMessage.value = globals.renderError(t, error)),
    );
  }
  resetOtpModal.value = false;
  currentUser.value = null;
};

function handleSave([_, err, isEdit]) {
  if (err) {
    errorMessage.value = globals.renderError(t, err);
    return;
  }

  currentUser.value = null;
  fetchData();
  infoMessage.value = isEdit ? t("msg.userUpdated") : t("msg.userCreated");
}

const sortBy = ref("Name");
const sortDesc = ref(false);

function handleSortChange(column) {
  if (sortBy.value === column) {
    sortDesc.value = !sortDesc.value;
    return;
  }

  sortBy.value = column;
  sortDesc.value = false;
}

const filterData = computed(() => {
  if (!users.value) return [];
  const searchTerm = search.value?.toString().toLocaleLowerCase() ?? "";

  const filtered = users.value.filter((item) => {
    if (!searchTerm) return true;
    return Object.values(item).some((val) => val?.toString().toLocaleLowerCase().includes(searchTerm) ?? false);
  });

  const sorted = [...filtered].sort(globals.smartSortFunc(globals.sortObjBy(sortBy.value)));
  return sortDesc.value ? sorted.reverse() : sorted;
});

const invalidateSessions = () => {
  userService.invalidateAllSessions().then(() => (infoMessage.value = t("msg.allUserSessionsInvalidated")));
};

const deleteSession = (id) => {
  userService.deleteSession(id).then(() => {
    infoMessage.value = t("msg.userSessionDeleted");
    fetchUserData(currentUser.value.ID);
  });
};

const fetchUserData = (id) => {
  userService.getUserGroups(id).then(
    (response) => {
      currentUserGroups.value = response.data;
    },
    (error) => {
      errorMessage.value = globals.renderError(t, error);
    },
  );

  userService.getUserAcls(id).then(
    (response) => {
      currentUserAcls.value = response.data;
    },
    (error) => {
      errorMessage.value = globals.renderError(t, error);
    },
  );

  userService.getUserSessions(id).then(
    (response) => {
      currentUserSessions.value = response.data;
    },
    (error) => {
      errorMessage.value = globals.renderError(t, error);
    },
  );
};

const fetchData = () => {
  userStore.fetchUsers({ force: true }).then(
    () => {},
    (error) => {
      errorMessage.value = globals.renderError(t, error);
    },
  );
  adminStore.fetchStatus({ force: true });
};

let userInterval;

watch(initialFocus, async (el) => {
  if (el) {
    await nextTick();
    el.focus();
  }
});

onMounted(() => {
  fetchData();
  userInterval = setInterval(() => fetchData(), 30 * 1000);
  window.addEventListener("scroll", updateMenuItemPositions);
  window.addEventListener("resize", updateMenuItemPositions);
});

onUnmounted(() => {
  clearInterval(userInterval);
  window.removeEventListener("scroll", updateMenuItemPositions);
  window.removeEventListener("resize", updateMenuItemPositions);
});

watch(() => menuButtonsRefs.value.length, updateMenuItemPositions);
</script>
